import { useEffect, useRef } from 'react'
import { getServiceApiUrl, type UserPreferences } from '@airclou/utils'
import { isLiveImpersonation } from '@airclou/auth-react'
import { useUserPreferences } from './UserPreferencesProvider'

/**
 * Connects this app's preferences to the account-wide ones held by Auth.
 *
 * Without it, a preference is per-app: each app namespaces its own localStorage
 * (`helpdesk:timezone`, `dashboard:timezone`) and some kept a server-side row of
 * their own, so a timezone chosen in one app meant nothing in the next — and an
 * app with no row of its own (dashboard) kept the choice in one browser only.
 * This reads the person's `user_settings` row from Auth, feeds it in as the
 * layer below anything this app stores itself, and writes every change back
 * there, so one choice backs every app.
 *
 * Mount it anywhere below `UserPreferencesProvider` that only renders for a
 * signed-in user, and pass the user id — most apps mount the provider above
 * their auth provider so public pages are themed, which is exactly where no
 * session exists yet. Passing the id rather than checking for a token at mount
 * is deliberate: the token is bootstrapped asynchronously from the session
 * cookie, so a mount-time check races the bootstrap and never re-runs. Keying on
 * the id also re-reads on a user switch or impersonation.
 *
 * Rendered as a sibling of the app's content, like `LanguageSync`. It draws
 * nothing.
 */
export interface GlobalPreferencesSyncProps {
  /** The signed-in user. Undefined/null while unauthenticated — the sync idles. */
  userId?: string | null
}

/** Long enough for a slow phone, short enough not to hang a page load. */
const REQUEST_TIMEOUT_MS = 15000

function readToken(appSlug: string): string | null {
  try {
    return localStorage.getItem(`${appSlug}:authToken`)
  } catch {
    return null
  }
}

export function GlobalPreferencesSync({ userId }: GlobalPreferencesSyncProps) {
  const { appSlug, applyGlobalDefaults, setGlobalSaver } = useUserPreferences()
  /**
   * The user whose account this component has touched — read into the
   * provider, or written to by a save that succeeded. It decides whether
   * ending this effect has anything to clear: a session is "over" when the
   * provider holds that person's answers or changes and the identity is going
   * away. A mount that touched nothing (StrictMode's dev-only
   * mount/unmount/mount, a very fast sign-out, an unreachable Auth with no
   * save) has put nothing in that needs taking out — and the cache it would
   * have taken out belongs to whoever is about to be read, in the one window
   * it exists to paint. A save counts as well as a read: a session whose read
   * failed can still have changed a key, and the next person's read must not
   * be told to skip it.
   */
  const touchedFor = useRef<string | null>(null)

  useEffect(() => {
    if (!userId) {
      // A stale saver would write this person's choices under a token that no
      // longer names them. The clearing itself happened in the cleanup of the
      // effect run that read for the previous user.
      setGlobalSaver(null)
      return
    }

    // Impersonation is a legitimate two-identity state and every identity
    // comparison has to skip it. Here that means idling: the token names the
    // impersonated person, so reading would pull *their* language and timezone
    // into the admin's browser, and every save would be refused by Auth as
    // read-only. The admin keeps their own local values for the duration.
    if (isLiveImpersonation(readToken(appSlug))) {
      setGlobalSaver(null)
      return
    }

    const authApiUrl = getServiceApiUrl('auth')
    let cancelled = false

    // Every app-originated call carries its own Bearer token. Never the session
    // cookie alone: `app.airclou.com` hosts several apps behind one cookie, so a
    // cookie-only write would edit whoever signed in last, anywhere in the suite.
    setGlobalSaver(async (key, value) => {
      const token = readToken(appSlug)
      // Throw rather than resolve: a save that quietly did nothing is
      // indistinguishable from one that worked until the next page load. This
      // happens when an idle-timeout logout clears the token under a page the
      // operator still has open.
      if (!token) throw new Error('No token for this app — account-wide preference not saved')
      const response = await fetch(`${authApiUrl}/auth/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ preferences: { [key]: value } }),
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      })
      if (!response.ok) {
        // The choice is already stored in this browser and, where the app has
        // one, in its own row — so the setting works here and the account-wide
        // copy is what did not update. Say which, rather than reporting a
        // failure the user can see is not total.
        throw new Error(`Failed to save account-wide preference (${response.status})`)
      }
      // A save that lands after this effect ended belongs to a session that is
      // over; recording it would make the next person's cleanup skip.
      if (!cancelled) touchedFor.current = userId
    })

    const load = async () => {
      // In every app the user id arrives from a context that read this token
      // first, so an absent one here means the session is already going away.
      const token = readToken(appSlug)
      if (!token) return
      try {
        const response = await fetch(`${authApiUrl}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        })
        if (!response.ok) {
          // Same cost as a network failure — the app keeps its own values — but
          // say so, or an expired token reads as "this person has no
          // preferences" and nobody ever learns otherwise.
          console.error('Failed to load account-wide preferences:', response.status)
          return
        }
        const body = await response.json()
        const preferences = (body?.data?.user ?? body?.user)?.preferences as
          | Partial<UserPreferences>
          | undefined
        // An Auth that predates the field sends nothing; leaving the layer null
        // keeps the app on its own values rather than resetting them to system
        // defaults.
        if (cancelled || !preferences) return
        applyGlobalDefaults(preferences)
        touchedFor.current = userId
      } catch (err) {
        // A read that fails costs the account-wide layer only — localStorage
        // and the app's own row still answer, so the page is usable and the
        // next load tries again.
        console.error('Failed to load account-wide preferences:', err)
      }
    }
    void load()

    return () => {
      cancelled = true
      setGlobalSaver(null)
      // Sign-out, user switch, impersonation, or the subtree unmounting —
      // dashboard mounts this under its ProtectedRoute, so a session ending
      // there tears the component down rather than passing null. All the same
      // event: the person this effect read for is going away, and what it
      // read must go with them, or the next person to sign in on this browser
      // reads in the last person's language.
      if (touchedFor.current === userId) {
        touchedFor.current = null
        applyGlobalDefaults(null)
      }
    }
  }, [userId, appSlug, applyGlobalDefaults, setGlobalSaver])

  return null
}
